import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';

import { joinClassNames } from '../../../util/util';
import { usePlayer } from '../../../contexts/PlayerContext';

interface ChaptersProps {
	chapters: any[];
	className?: string;
	tabIndex?: number;
	color?: string;
	isMobile?: boolean;
	showHoverPlayer?: boolean;
	bottomControlBarRef?: any;
	hoverTiming?: number;
	isDragging?: boolean;

	onClick?: (_event: React.MouseEvent<HTMLElement>) => void;
	onMouseEnter?: (_event: React.MouseEvent, _chapter?: any) => void;
	onMouseLeave?: (_event: React.MouseEvent) => void;
	onMouseDown?: (_event: React.MouseEvent<HTMLElement>) => void;
	onKeyDown?: (_event: React.KeyboardEvent) => void;
	onFocus?: (_event: React.FocusEvent) => void;
	onBlur?: (_event: React.FocusEvent) => void;
}

const Chapters = ({
	chapters,
	className = undefined,
	tabIndex,
	color = undefined,
	isMobile = false,
	showHoverPlayer,
	hoverTiming = 0,
	isDragging,
	onClick,
	onMouseEnter,
	onMouseLeave,
	onMouseDown,
	onKeyDown,
	onFocus,
	onBlur,
}: ChaptersProps) => {
	const { t } = useTranslation();

	const { currentPosition: pos } = usePlayer();
	const currentPosition = typeof pos === 'number' ? pos : 0;

	const [hoveredPosition, setHoveredPosition] = useState<number>(0);
	const [hoveredChapter, setHoveredChapter] = useState<any | null>(null);

	const hoveredChapterIndex = useMemo(
		() => {
			const newHoveredChapterIndex = chapters.length > 0 ? chapters.findIndex((i) => i.id === hoveredChapter?.id) : -1;
			if (hoverTiming < hoveredChapter?.start) {
				return newHoveredChapterIndex >= 1 ? newHoveredChapterIndex - 1 : newHoveredChapterIndex;
			}
			return newHoveredChapterIndex;
		},
		[hoverTiming, hoveredChapter, chapters]
	);

	const getProgressPercentOfChapter = useCallback(
		(chapter, isActive) => {
			if (!isActive) {
				return '';
			}

			const chapterRef = document.querySelector('.qplayer-chapter--active');
			if (!chapterRef) {
				return '';
			}

			const chapterLength = chapter.end - chapter.start;
			let currentPositionInChapter = currentPosition - chapter.start;
			const lengthPercent = (currentPositionInChapter / chapterLength) * 100;
			return lengthPercent + '%';
		},
		[currentPosition]
	);

	const setDraggingStyle = useCallback(() => {
		if (!hoverTiming) {
			return;
		}

		const chaptersRef = document.querySelectorAll('.qplayer-chapter');
		chapters.map((chapter, index) => {
			const chapterRef = chaptersRef[index];
			if (!chapterRef) {
				return;
			}
			const chapterProgress = chapterRef.querySelector('.qplayer-chapter-progress');
			if (!chapterProgress) {
				return;
			}

			if (chapter.end <= hoverTiming) {
				(chapterProgress as any).style.width = '';
				(chapterProgress as any).style.backgroundColor = color;
			} else if (chapter.start <= hoverTiming && hoverTiming <= chapter.end) {
				const chapterLength = chapter.end - chapter.start;
				let currentPositionInChapter = hoverTiming - chapter.start;
				const lengthPercent = (currentPositionInChapter / chapterLength) * 100;
				(chapterProgress as any).style.width = lengthPercent + 1 + '%';
				(chapterProgress as any).style.backgroundColor = color;
			} else {
				(chapterProgress as any).style.width = '';
				(chapterProgress as any).style.backgroundColor = '';
			}
		});
	}, [chapters, hoverTiming, color]);

	const handleMouseMoveProgressBar = (e: React.MouseEvent) => {
		if (!hoveredChapter) {
			return;
		}
		const chapterWrapper = document.querySelector('.qplayer-chapters')?.getBoundingClientRect() ?? ({} as any);
		const chapterWrappers = document.getElementsByClassName('qplayer-chapter');

		let delta = 0;
		chapters.forEach((d, i) => {
			if (i > hoveredChapterIndex) {
				delta += chapterWrappers[i].getBoundingClientRect().width;
			}
		});

		const y = chapterWrapper.width - e.clientX - delta;
		const hoveredChapterWidth = chapterWrappers[hoveredChapterIndex].getBoundingClientRect().width;
		const yPercent = (y / hoveredChapterWidth) * 100;
		setHoveredPosition(100 - Math.abs(yPercent));
	};

	const onMouseEnterChapter = useCallback(
		(e, chapter) => {
			setHoveredChapter(chapter);
			onMouseEnter?.(e, chapter);
		},
		[onMouseEnter]
	);

	const onMouseLeaveChapter = useCallback(
		(e) => {
			setHoveredChapter(null);
			onMouseLeave?.(e);
		},
		[setHoveredChapter, onMouseLeave]
	);

	useEffect(() => {
		if (isMobile || hoverTiming === undefined || isDragging) {
			return;
		}

		// Make the chapter bigger when hovering on the indicator
		if (!showHoverPlayer || !(hoverTiming - 2 <= currentPosition && currentPosition <= hoverTiming + 2)) {
			return;
		}
		chapters.map((chapter) => {
			if (chapter.start <= currentPosition && currentPosition <= chapter.end) {
				const chapterRef = document.querySelector('.qplayer-chapter--active');
				chapterRef && chapterRef.classList.add('qplayer-chapter--hovered');
			}
		});
	}, [currentPosition, chapters, hoverTiming, isDragging, isMobile, showHoverPlayer]);

	useEffect(() => {
		if (!isDragging) {
			return;
		}

		setDraggingStyle();
	}, [setDraggingStyle, isDragging]);

	return (
		<div
			className={joinClassNames('qplayer-chapters', isMobile ? 'qplayer-chapters--mobile' : '', className)}
			tabIndex={tabIndex}
			aria-label={t('PROGRESS_BAR')}
			aria-valuemin={0}
			role="slider"
			aria-valuenow={Math.abs(currentPosition)}
			onClick={onClick}
			onKeyDown={onKeyDown}
			onMouseEnter={onMouseEnter}
			onMouseDown={onMouseDown}
			onMouseUp={(e) => e.stopPropagation}
			onMouseMove={handleMouseMoveProgressBar}
			onFocus={onFocus}
			onBlur={onBlur}
		>
			{chapters.map((chapter, i) => {
				const isActiveChapter = chapter.start <= currentPosition && currentPosition <= chapter.end;
				const isActiveByDragging = isDragging && (chapter.end <= hoverTiming || (chapter.start <= hoverTiming && hoverTiming <= chapter.end));

				return (
					<div
						className={joinClassNames(
							'qplayer-chapter',
							isActiveChapter ? 'qplayer-chapter--active' : '',
							(isDragging && chapter.start <= hoverTiming && hoverTiming <= chapter.end) ||
								(!isDragging && hoveredChapterIndex === i)
								? 'qplayer-chapter--hovered'
								: ''
						)}
						key={i}
						style={{ width: chapter.width + '%' }}
						onMouseEnter={(e) => onMouseEnterChapter(e, chapter)}
						onMouseLeave={onMouseLeaveChapter}
					>
						{!isMobile && (
							<div
								className={joinClassNames(
									'qplayer-chapter-hover-progress',
									(hoveredChapterIndex >= 0 && i <= hoveredChapterIndex) || isActiveByDragging
										? 'qplayer-chapter-hover-progress--active'
										: ''
								)}
								style={
									hoveredPosition && i <= hoveredChapterIndex && !isDragging
										? {
											width: i === hoveredChapterIndex ? hoveredPosition + '%' : '',
											backgroundColor: tinycolor(color).lighten(30).toString(),
										}
										: {}
								}
							/>
						)}
						<div className={'qplayer-chapter-progress--base'} />
						<div
							className={joinClassNames(
								'qplayer-chapter-progress',
								isMobile ? 'qplayer-chapter-progress--mobile' : '',
								currentPosition >= chapter.end || isActiveChapter || isActiveByDragging ? 'qplayer-chapter-progress--active' : ''
							)}
							style={isDragging ? {} : { width: getProgressPercentOfChapter(chapter, isActiveChapter), backgroundColor: color }}
						/>
					</div>
				);
			})}
		</div>
	);
};

export default Chapters;
